// App.jsx
import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import "./App2.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { getSubdomain } from "./libs/helper";
import { DOMAIN_NAME, IS_DEV } from "./libs/axiosInstance";
import { loadAuth } from "./libs/authMgmt";
import Swal from "sweetalert2";
import FindSubdomain from "./pages/findSubdomain";
import ResetPassword from "./pages/resetPassword";
import AiChat from "./pages/aiChatbot";
import MyShopPage from "./pages/MyShop/main";
import ExpenseTable from "./pages/manageExpenses";
import MainPage from "./pages/dashboardV2";
import PaymentSettings from "./pages/paymentSettings";
import PublicOrderPage from "./pages/public_pages/orderingPage";
import { useParams } from "react-router-dom";
import MenuList from "./pages/kitchen/menuList";
import MenuPage from "./pages/kitchen/menuPage";
import MenuEdit from "./pages/kitchen/menuEdit";
import CategoryItems from "./pages/kitchen/categoryItems";
import KitchenBoard from "./pages/kitchen/kitchenBoard";

// Lazy pages
const Dashboard = lazy(() => import("./pages/dashboard"));
const History = lazy(() => import("./pages/history"));
const InventoryPage = lazy(() =>
  import("./components/inventory/InventoryTable")
);
const PromptPage = lazy(() => import("./pages/promptPage"));
const POSPage = lazy(() => import("./pages/pos"));
const SalesHistory = lazy(() => import("./pages/salesHistory"));
const Login = lazy(() => import("./pages/login"));
const OnboardingReturn = lazy(() => import("./pages/onboardingStatus"));
const RegisterCompany = lazy(() => import("./pages/register"));
const BillingSuccess = lazy(() => import("./pages/billingSuccess"));
const BillingCancel = lazy(() => import("./pages/billingCancel"));
const Settings = lazy(() => import("./pages/settingsPanel"));
const LogOut = lazy(() => import("./pages/logout"));
const LandingPage = lazy(() => import("./pages/landingPage"));
const LandingHome = lazy(() => import("./pages/landing/home/home"));
const LandingPrivacy = lazy(() => import("./pages/landing/privacy/privacy"));
const LandingTerms = lazy(() => import("./pages/landing/terms/terms"));
const LandingFeatures = lazy(() => import("./pages/landing/features/features"));
const LandingTypes = lazy(() =>
  import("./pages/landing/business-types/businessTypes")
);
const LandingAbout = lazy(() => import("./pages/landing/about/about"));
const Accounts = lazy(() => import("./pages/accountManagement"));
const PromptResponse = lazy(() => import("./pages/customPrompt"));
const AdsExample = lazy(() => import("./components/ads/AdExample"));

function Loader() {
  return (
    <div className="d-flex align-items-center justify-content-center vh-100">
      <div className="text-center">
        <div className="spinner-border" role="status" aria-label="Loading" />
        <div className="mt-2 text-muted small">Loading…</div>
      </div>
    </div>
  );
}

function LandingOrDashboard() {
  return getSubdomain() === "" ? <LandingHome /> : <Dashboard />;
}

export default function App() {
  useEffect(() => {
    // CSS Cache busting - Add version parameter to existing CSS links
    const addVersionToCSS = () => {
      const cssVersion = Date.now();
      const links = document.querySelectorAll('link[rel="stylesheet"]');

      links.forEach((link) => {
        const href = link.getAttribute("href");
        if (href && href.includes("App") && !href.includes("?v=")) {
          link.setAttribute("href", `${href}?v=${cssVersion}`);
          console.log(`🎨 CSS cache busted with version: ${cssVersion}`);
        }
      });
    };

    // Run after a short delay to ensure CSS is loaded
    setTimeout(addVersionToCSS, 100);

    // check localstorage if logged vercel update
    let subdomain = loadAuth("company")?.subdomain ?? null;
    const isLoggedIn = IS_DEV
      ? subdomain
      : (subdomain?.toLowerCase() ?? "nope") ===
        (getSubdomain()?.toLowerCase() ?? "nuh uhh");

    if (!(IS_DEV && !isLoggedIn && window.location.pathname === "/")) {
      //first check if no subdomain, then redirect them to their logged in subdomain if logged in
      if (
        IS_DEV === false &&
        getSubdomain() === "" &&
        window.location.pathname === "/" &&
        subdomain
      ) {
        Swal.fire({
          title: "Welcome back!",
          text: "It looks like you're still signed in. Would you like to go to your account dashboard now?",
          icon: "warning",
          showCancelButton: true,
          // confirmButtonColor: "#3085d6",
          // cancelButtonColor: "#d33",
          confirmButtonText: "Yes, take me to my account",
          cancelButtonText: "Stay here",
        }).then((result) => {
          if (result.isConfirmed) {
            Swal.fire("Redirecting..", "Please wait a moment", "info");
            window.location.replace(
              `https://${subdomain}.${DOMAIN_NAME}/dashboard`
            );
          } else if (result.dismiss === Swal.DismissReason.cancel) {
          }
        });
      } else if (
        (window.location.pathname === "/login" ||
          window.location.pathname === "/register") &&
        isLoggedIn
      ) {
        if (IS_DEV) {
          window.location.replace("/dashboard");
        } else if (getSubdomain() !== "") {
          window.location.replace(
            `https://${getSubdomain()}.${DOMAIN_NAME}/dashboard`
          );
        } else {
          window.location.replace(
            `https://${subdomain}.${DOMAIN_NAME}/dashboard`
          );
        }
      } else if (
        (window.location.pathname === "/" ||
          window.location.pathname === "/dashboard") &&
        !isLoggedIn
      ) {
        if (IS_DEV) {
          window.location.replace("/login");
        } else if (getSubdomain() !== "") {
          window.location.replace(
            `https://${getSubdomain()}.${DOMAIN_NAME}/login`
          );
        } else if (window.location.pathname === "/dashboard") {
          window.location.replace(`https://${DOMAIN_NAME}`);
        }
      }
    }
  }, []);
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path="/" element={<LandingOrDashboard />} />
        <Route path="/dashboard" element={<MainPage />} />
        {/* <Route path="/demo-dashboard" element={<MainPage />} /> */}
        <Route path="/privacy" element={<LandingPrivacy />} />
        <Route path="/about" element={<LandingAbout />} />
        <Route path="/terms" element={<LandingTerms />} />{" "}
        <Route path="/ads" element={<AdsExample />} />
        <Route path="/features" element={<LandingFeatures />} />
        <Route path="/business-types" element={<LandingTypes />} />
        <Route path="/history" element={<History />} />
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/prompts/:promptId" element={<PromptPage />} />
        <Route path="/custom-prompt" element={<PromptResponse />} />
        <Route path="/pos" element={<POSPage />} />
        <Route path="/sales-history" element={<SalesHistory />} />
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<LogOut />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/user-roles" element={<Accounts />} />
        <Route path="/my-shop" element={<MyShopPage />} />
        <Route path="/manage-expenses" element={<ExpenseTable />} />
        <Route
          path="/order/:tableId/:menuId"
          element={<WrappedPublicOrderPage />}
        />
        <Route path="/manage-menu" element={<MenuPage />} />
        <Route path="/manage-menu/:menuId/edit" element={<WrappedEditPage />} />
        <Route
          path="/manage-menu/:menuId/items"
          element={<WrappedItemsPage />}
        />
        <Route path="/kitchen" element={<KitchenBoard />} />
        <Route
          path="/ai"
          element={
            <AiChat className="" label="AI Chat" value="" width="100%" />
          }
        />
        <Route
          path="/settings/onboarding/return"
          element={<OnboardingReturn />}
        />
        <Route path="/register" element={<RegisterCompany />} />
        <Route path="/billing/success" element={<BillingSuccess />} />
        <Route path="/billing/cancel" element={<BillingCancel />} />
        <Route path="/find-subdomain" element={<FindSubdomain />} />
        <Route path="/forgot-password" element={<ResetPassword />} />
      </Routes>
    </Suspense>
  );
}
function WrappedItemsPage() {
  const { menuId } = useParams();
  return <CategoryItems menuId={menuId} />;
}
function WrappedEditPage() {
  const { menuId } = useParams();
  return <MenuEdit menuId={menuId} />;
}
function WrappedPublicOrderPage() {
  const { tableId, menuId } = useParams();
  return <PublicOrderPage tableId={tableId} menuId={menuId} />;
}
